import { Spinner, Text, Heading, VStack } from "@chakra-ui/react";
import { ProblemCode } from "@syla/shared/errors/ProblemCode";
import { isSylaApiError } from "@syla/shared/errors/SylaApiError";
import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";

import { UserContext } from "../../../contexts/UserContext";
import { Route, useNavigator } from "../../../routers/navigator";
import { useMutationVerifyAccountInvite } from "../../../store/useMutationVerifyAccountInvite";
import { FormContainer } from "../../atoms/Containers";
import { UnexpectedError } from "../../atoms/UnexpectedError";
import { SylaFormHeader } from "../forms/SylaFormHeader";
import { AccountInviteNewUserForm } from "./AccountInviteNewUserForm";

export const VerifyAccountInvite = (): JSX.Element => {
  const { setUserDetails, setFirstLogin } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigator();

  const [errorState, setErrorState] = useState<unknown | string>();

  const { mutateAsync: verifyAccountInvite, isLoading } =
    useMutationVerifyAccountInvite();

  const verifyAccountInviteAction = (password?: string) => {
    const searchParams = new URLSearchParams(location.search);
    const token = searchParams.get("token");

    if (!token) {
      setErrorState("no token");
      return;
    }

    verifyAccountInvite({ token, password })
      .then((response) => {
        setFirstLogin(true); // needed for correct transition to authenticated state

        setUserDetails({
          accessToken: response.auth.token,
          refreshToken: response.auth.refreshToken,
          email: response.auth.email,
          id: response.auth.userId,
          defaultAccountId: response.auth.accountId,
        });

        navigate({
          route: Route.Home,
          accountId: response.auth.accountId,
          replace: true,
        });
      })
      .catch((error: unknown) => {
        setErrorState(error);
      });
  };

  // do initial verify on load
  useEffect(() => {
    verifyAccountInviteAction();
  }, []);

  function renderError(error: unknown): JSX.Element {
    const errorColor = "red.500";

    if (isSylaApiError(error)) {
      if (error.code === ProblemCode.InviteInvalid) {
        return (
          <Text color={errorColor}>
            This invite is no longer valid. Please sign in if you already have
            access, or ask the account owner for access again.
          </Text>
        );
      }

      if (error.code === ProblemCode.InviteExpired) {
        return (
          <Text color={errorColor}>
            {
              "This link has expired. We've sent another link to your email address."
            }
          </Text>
        );
      }

      if (error.code === ProblemCode.InvitePasswordRequired) {
        return (
          <VStack w="full" align="flex-start" spacing={8}>
            <Heading size="sm">
              Please set a password for your new user profile
            </Heading>
            <AccountInviteNewUserForm
              onSetPassword={({ newPassword }) => {
                return verifyAccountInviteAction(newPassword);
              }}
            />
          </VStack>
        );
      }
    }

    return <UnexpectedError />;
  }

  return (
    <FormContainer>
      <SylaFormHeader
        heading={!isLoading ? "Account Invite" : "Please wait..."}
      />

      {isLoading && (
        <Spinner
          color="primary.500"
          emptyColor="primary.200"
          my="10px"
          thickness="5px"
          size="lg"
        />
      )}
      {errorState && renderError(errorState)}
    </FormContainer>
  );
};
